import { endAndReportPerformanceMeasure, reportError, startPerformanceMeasure } from '@ms/yammer-telemetry';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';

import type { GlobalCopilotEntrypoint } from '../../telemetry/types';

import type { GlobalCopilotInstance } from './globalCopilotInstanceStore';
import { useGlobalCopilotInstanceStore } from './globalCopilotInstanceStore';
import { useGlobalCopilotStore } from './globalCopilotStore';

export interface OfficeAICopilotState {
  readonly open: () => Promise<Exclude<GlobalCopilotInstance, null>>;
  readonly openFromEntrypoint: (entrypoint: GlobalCopilotEntrypoint) => ReturnType<OfficeAICopilotState['open']>;
  readonly close: () => void;
  readonly isHistoryButtonDisabled: boolean;
  readonly setIsHistoryButtonDisabled: (isHistoryButtonDisabled: boolean) => void;
}

const getInitialOfficeAIState = (): Pick<OfficeAICopilotState, 'isHistoryButtonDisabled'> => ({
  isHistoryButtonDisabled: false,
});

export const resetOfficeAICopilotState = (): void => {
  useOfficeAICopilotStore.setState(getInitialOfficeAIState());
};

export const useOfficeAICopilotStore: UseBoundStore<StoreApi<OfficeAICopilotState>> = create<OfficeAICopilotState>(
  (set, get) => ({
    ...getInitialOfficeAIState(),

    open: async () => {
      const componentMeasureName = 'global_copilot_cold_load_e2e';
      const measure = startPerformanceMeasure('ui_component_measure', componentMeasureName);
      const instance = await useGlobalCopilotInstanceStore.getState().instancePromise;

      if (instance == null) {
        reportError({
          error: new Error('GlobalCopilotInitFailed'),
          eventProperties: { errorCode: 'GlobalCopilotInitFailed' },
        });

        return Promise.reject(new Error('GlobalCopilotInitFailed'));
      }

      endAndReportPerformanceMeasure({
        ...measure,
        eventProperties: { ...measure.eventProperties, componentMeasureName },
      });

      return instance;
    },

    openFromEntrypoint: async (entrypoint) => {
      useGlobalCopilotStore.getState().openDrawer(entrypoint);

      return get().open();
    },

    close: () => {
      useGlobalCopilotStore.getState().close();
      useGlobalCopilotInstanceStore.getState().resetInstancePromise();
      resetOfficeAICopilotState();
    },

    setIsHistoryButtonDisabled: (isHistoryButtonDisabled) => {
      set({ isHistoryButtonDisabled });
    },
  })
);
